import React, {
  useMemo,
} from "react";

import DOMPurify from "dompurify";

import {
  FiAlertCircle,
  FiClock,
  FiInbox,
  FiMessageSquare,
  FiUser,
} from "react-icons/fi";

import {
  cn,
  formatDateTime,
  formatNumber,
  getInitials,
} from "../utils/siteActivityFormatters";

/* ==========================================================================
   HELPERS
   ========================================================================== */

function stripHtml(html = "") {
  if (
    typeof document ===
    "undefined"
  ) {
    return String(
      html || "",
    ).replace(
      /<[^>]+>/g,
      " ",
    );
  }

  const element =
    document.createElement(
      "div",
    );

  element.innerHTML =
    DOMPurify.sanitize(
      html,
    );

  return (
    element.textContent ||
    element.innerText ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim();
}

function getItemDate(item = {}) {
  return (
    item.created_at ||
    item.createdAt ||
    item.date ||
    item.date_message ||
    item.updated_at ||
    item.updatedAt ||
    ""
  );
}

function getTimestamp(item) {
  const value =
    getItemDate(item);

  if (!value) {
    return 0;
  }

  const timestamp =
    new Date(value).getTime();

  return Number.isFinite(
    timestamp,
  )
    ? timestamp
    : 0;
}

function getLatestItem(items = []) {
  if (!Array.isArray(items)) {
    return null;
  }

  return [...items].sort(
    (a, b) =>
      getTimestamp(b) -
      getTimestamp(a),
  )[0] || null;
}

function getMessagePreview(
  message = {},
) {
  return stripHtml(
    message.content ||
      message.message ||
      message.description ||
      "",
  );
}

function getMessageSender(
  message = {},
) {
  return (
    message.name ||
    message.sender_name ||
    message.nom ||
    message.prenom ||
    message.email ||
    message.mail ||
    ""
  );
}

function getRequestPreview(
  request = {},
) {
  return stripHtml(
    request.description ||
      request.content ||
      request.message ||
      "",
  );
}

function getRequestTitle(
  request = {},
) {
  return (
    request.title ||
    request.titre ||
    "Requête client"
  );
}

/* ==========================================================================
   LOGO
   ========================================================================== */

function SiteLogo({
  src,
  name,
}) {
  if (!src) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-xs font-black text-[#123B57] dark:bg-slate-800 dark:text-white">
        {getInitials(
          name,
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-11 w-11 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
      onError={(event) => {
        event.currentTarget.style.display =
          "none";
      }}
    />
  );
}

/* ==========================================================================
   MESSAGE SITE
   ========================================================================== */

function MessageSiteCard({
  row,
  onOpen,
}) {
  const messages =
    row.messages || [];

  const latest =
    getLatestItem(
      messages,
    );

  const preview =
    getMessagePreview(
      latest,
    );

  const sender =
    getMessageSender(
      latest,
    );

  return (
    <article className="overflow-hidden rounded-[22px] border border-sky-200 bg-white shadow-sm transition hover:border-sky-300 hover:shadow-md dark:border-sky-900/50 dark:bg-slate-900">
      <div className="h-1 bg-gradient-to-r from-sky-500 to-cyan-400" />

      <div className="p-4">
        <div className="flex items-start gap-3">
          <SiteLogo
            src={row.logo}
            name={row.name}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="truncate text-sm font-black text-[#123B57] dark:text-white">
                  {row.name}
                </h4>

                {sender && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    <FiUser />

                    <span className="truncate">
                      {sender}
                    </span>
                  </div>
                )}
              </div>

              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sky-100 px-2.5 py-1 text-xs font-black text-sky-700 dark:bg-sky-950/50 dark:text-sky-200">
                <FiMessageSquare />

                {formatNumber(
                  messages.length,
                )}
              </span>
            </div>

            <div className="mt-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/60">
              <p className="line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {preview ||
                  "Message sans aperçu."}
              </p>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                <FiClock />

                {formatDateTime(
                  getItemDate(
                    latest,
                  ),
                )}
              </div>

              <button
                type="button"
                onClick={onOpen}
                className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-sky-700"
              >
                <FiMessageSquare />

                Voir{" "}
                {messages.length >
                1
                  ? "les messages"
                  : "le message"}
              </button>
            </div>

            {row.criticalDataStale && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                <FiAlertCircle />

                Dernières données
                connues
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

/* ==========================================================================
   REQUÊTE SITE
   ========================================================================== */

function RequestSiteCard({
  row,
  onOpen,
}) {
  const requests =
    row.requests || [];

  const latest =
    getLatestItem(
      requests,
    );

  const preview =
    getRequestPreview(
      latest,
    );

  const title =
    getRequestTitle(
      latest,
    );

  const highCount =
    Number(
      row.requestsHigh ||
        0,
    );

  return (
    <article className="overflow-hidden rounded-[22px] border border-amber-200 bg-white shadow-sm transition hover:border-amber-300 hover:shadow-md dark:border-amber-900/50 dark:bg-slate-900">
      <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-400" />

      <div className="p-4">
        <div className="flex items-start gap-3">
          <SiteLogo
            src={row.logo}
            name={row.name}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="truncate text-sm font-black text-[#123B57] dark:text-white">
                  {row.name}
                </h4>

                <div className="mt-1 truncate text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {title}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-black text-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
                  <FiInbox />

                  {formatNumber(
                    requests.length,
                  )}
                </span>

                {highCount >
                  0 && (
                  <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-black text-rose-700 dark:bg-rose-950/50 dark:text-rose-200">
                    {highCount} urgente
                    {highCount >
                    1
                      ? "s"
                      : ""}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/60">
              <p className="line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {preview ||
                  "Aucune description."}
              </p>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                <FiClock />

                {formatDateTime(
                  getItemDate(
                    latest,
                  ),
                )}
              </div>

              <button
                type="button"
                onClick={onOpen}
                className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-3 py-2 text-xs font-bold text-white transition hover:bg-amber-600"
              >
                <FiInbox />

                Voir{" "}
                {requests.length >
                1
                  ? "les requêtes"
                  : "la requête"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ==========================================================================
   COLONNE
   ========================================================================== */

function InboxColumn({
  icon,
  title,
  subtitle,
  count,
  tone,
  children,
  emptyText,
}) {
  const toneClasses = {
    sky: {
      badge:
        "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-200",

      icon:
        "bg-gradient-to-br from-sky-500 to-cyan-400",
    },

    amber: {
      badge:
        "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200",

      icon:
        "bg-gradient-to-br from-amber-500 to-orange-400",
    },
  };

  const config =
    toneClasses[tone] ||
    toneClasses.sky;

  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-950/30">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm",
              config.icon,
            )}
          >
            {icon}
          </div>

          <div className="min-w-0">
            <h3 className="font-black text-[#123B57] dark:text-white">
              {title}
            </h3>

            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>
        </div>

        <span
          className={cn(
            "inline-flex min-w-[38px] items-center justify-center rounded-full px-3 py-1.5 text-sm font-black",
            config.badge,
          )}
        >
          {formatNumber(
            count,
          )}
        </span>
      </div>

      {count > 0 ? (
        <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
          {children}
        </div>
      ) : (
        <div className="flex min-h-[150px] items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-white p-6 text-center text-sm font-semibold text-slate-400 dark:border-slate-800 dark:bg-slate-900">
          {emptyText}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
   COMPOSANT
   ========================================================================== */

export default function SiteActivityPriorityInbox({
  rows = [],
  onOpenDetails,
}) {
  const messageRows =
    useMemo(() => {
      return rows
        .filter(
          (row) =>
            Number(
              row.messages?.length ||
                0,
            ) > 0,
        )
        .sort((a, b) => {
          const latestA =
            getLatestItem(
              a.messages,
            );

          const latestB =
            getLatestItem(
              b.messages,
            );

          return (
            getTimestamp(
              latestB,
            ) -
            getTimestamp(
              latestA,
            )
          );
        });
    }, [rows]);

  const requestRows =
    useMemo(() => {
      return rows
        .filter(
          (row) =>
            Number(
              row.requestsPending ||
                row.requests?.length ||
                0,
            ) > 0,
        )
        .sort((a, b) => {
          if (
            Number(
              b.requestsHigh ||
                0,
            ) !==
            Number(
              a.requestsHigh ||
                0,
            )
          ) {
            return (
              Number(
                b.requestsHigh ||
                  0,
              ) -
              Number(
                a.requestsHigh ||
                  0,
              )
            );
          }

          const latestA =
            getLatestItem(
              a.requests,
            );

          const latestB =
            getLatestItem(
              b.requests,
            );

          return (
            getTimestamp(
              latestB,
            ) -
            getTimestamp(
              latestA,
            )
          );
        });
    }, [rows]);

  const totalMessages =
    useMemo(() => {
      return messageRows.reduce(
        (total, row) =>
          total +
          Number(
            row.messages?.length ||
              0,
          ),
        0,
      );
    }, [messageRows]);

  const totalRequests =
    useMemo(() => {
      return requestRows.reduce(
        (total, row) =>
          total +
          Number(
            row.requestsPending ||
              row.requests?.length ||
              0,
          ),
        0,
      );
    }, [requestRows]);

  const totalToHandle =
    totalMessages +
    totalRequests;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-[28px] border bg-white shadow-sm dark:bg-slate-900",

        totalToHandle > 0
          ? "border-rose-200 ring-2 ring-rose-100 dark:border-rose-900/50 dark:ring-rose-950/40"
          : "border-slate-200 dark:border-slate-800",
      )}
    >
      <div
        className={cn(
          "border-b p-5 lg:p-6",

          totalToHandle > 0
            ? "border-rose-100 bg-gradient-to-r from-rose-50 via-white to-amber-50 dark:border-rose-900/40 dark:from-rose-950/30 dark:via-slate-900 dark:to-amber-950/20"
            : "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/30",
        )}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] text-xl text-white shadow-lg",

                totalToHandle > 0
                  ? "bg-gradient-to-br from-rose-500 to-orange-400"
                  : "bg-gradient-to-br from-emerald-500 to-teal-400",
              )}
            >
              <FiInbox />
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-black text-[#123B57] dark:text-white">
                  Boîte de réception
                  prioritaire
                </h2>

                {totalToHandle >
                  0 && (
                  <span className="animate-pulse rounded-full bg-rose-500 px-3 py-1 text-xs font-black uppercase tracking-wide text-white">
                    À traiter
                  </span>
                )}
              </div>

              <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                Tous les messages
                clients non lus et
                toutes les requêtes
                encore en attente,
                quelle que soit
                l'application
                actuellement
                consultée.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="rounded-2xl bg-sky-100 px-4 py-2 text-center dark:bg-sky-950/50">
              <div className="text-xl font-black text-sky-700 dark:text-sky-200">
                {formatNumber(
                  totalMessages,
                )}
              </div>

              <div className="text-[11px] font-bold uppercase tracking-wide text-sky-600 dark:text-sky-300">
                Message
                {totalMessages >
                1
                  ? "s"
                  : ""}
              </div>
            </div>

            <div className="rounded-2xl bg-amber-100 px-4 py-2 text-center dark:bg-amber-950/50">
              <div className="text-xl font-black text-amber-700 dark:text-amber-200">
                {formatNumber(
                  totalRequests,
                )}
              </div>

              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-300">
                Requête
                {totalRequests >
                1
                  ? "s"
                  : ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-2 lg:p-6">
        <InboxColumn
          icon={
            <FiMessageSquare />
          }
          title="Messages clients"
          subtitle="Tous les messages encore non lus"
          count={
            totalMessages
          }
          tone="sky"
          emptyText="Aucun message client non lu."
        >
          {messageRows.map(
            (row) => (
              <MessageSiteCard
                key={
                  row.id
                }
                row={row}
                onOpen={() =>
                  onOpenDetails({
                    type:
                      "messages",

                    siteName:
                      row.name,

                    items:
                      row.messages ||
                      [],
                  })
                }
              />
            ),
          )}
        </InboxColumn>

        <InboxColumn
          icon={<FiInbox />}
          title="Requêtes clients"
          subtitle="Toutes les demandes encore en attente"
          count={
            totalRequests
          }
          tone="amber"
          emptyText="Aucune requête client en attente."
        >
          {requestRows.map(
            (row) => (
              <RequestSiteCard
                key={
                  row.id
                }
                row={row}
                onOpen={() =>
                  onOpenDetails({
                    type:
                      "requests",

                    siteName:
                      row.name,

                    items:
                      row.requests ||
                      [],
                  })
                }
              />
            ),
          )}
        </InboxColumn>
      </div>
    </section>
  );
}